import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { PANEL_NAV, NavGroup } from '../config/panelNavConfig';
import { usePanelAccess } from '../context/PanelAccessContext';

type Props = {
  roleId: number;
  accessLoading: boolean;
  hasAccessToSection: (section: string) => boolean;
};

/* ── Icônes ─────────────────────────────────────────────────────────── */
function NavIcon({ name, className = 'h-[17px] w-[17px] shrink-0' }: { name: string; className?: string }) {
  const s = { strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, strokeWidth: 1.75 };
  switch (name) {
    case 'dashboard':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M3 11.5 12 4l9 7.5M5 10v9h14v-9" /></svg>;
    case 'users':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m18 0v-2a4 4 0 0 0-3-3.87M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>;
    case 'shield':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="m12 3 7 4v5c0 5-3.5 8.5-7 9-3.5-.5-7-4-7-9V7l7-4Z" /></svg>;
    case 'cta':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>;
    case 'logs':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z" /></svg>;
    case 'settings':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M10.5 3h3l.6 2.3c.5.2 1 .5 1.5.9l2.2-.8 1.5 2.6-1.7 1.6c.1.5.1 1 .1 1.5s0 1-.1 1.5l1.7 1.6-1.5 2.6-2.2-.8c-.5.4-1 .7-1.5.9L13.5 21h-3l-.6-2.3a6.7 6.7 0 0 1-1.5-.9l-2.2.8-1.5-2.6 1.7-1.6a9 9 0 0 1 0-3L4.7 9.8l1.5-2.6 2.2.8c.5-.4 1-.7 1.5-.9L10.5 3Zm4.5 9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>;
    case 'system':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M5 12h14M12 5l7 7-7 7" /></svg>;
    case 'referent':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2m22 0v-2a4 4 0 0 0-3-3.87m-4-12a4 4 0 0 1 0 7.75M14 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" /></svg>;
    default:
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden><path {...s} d="M4 6h16v12H4z" /></svg>;
  }
}

/* ── Chevron ─────────────────────────────────────────────────────────── */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="m6 9 6 6 6-6" />
    </svg>
  );
}

/* ── Composant principal ─────────────────────────────────────────────── */
const PanelSidebarNav: React.FC<Props> = ({ roleId, accessLoading, hasAccessToSection }) => {
  const location = useLocation();
  const { hasReferentFunction } = usePanelAccess();

  /* Groupes ouverts par défaut : ceux qui contiennent la route active */
  const getDefaultOpen = () => {
    const open = new Set<string>();
    for (const group of PANEL_NAV) {
      if (group.direct) continue;
      const items = group.items ?? [];
      const hasActive = items.some((item) => {
        const prefix = item.activePrefix ?? item.to;
        return location.pathname === item.to || location.pathname.startsWith(`${prefix}/`);
      });
      if (hasActive) open.add(group.id);
    }
    return open;
  };

  const [openGroups, setOpenGroups] = useState<Set<string>>(getDefaultOpen);

  /* Ouvrir automatiquement le groupe de la route active lors de navigation */
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      for (const group of PANEL_NAV) {
        if (group.direct) continue;
        const items = group.items ?? [];
        const hasActive = items.some((item) => {
          const prefix = item.activePrefix ?? item.to;
          return location.pathname === item.to || location.pathname.startsWith(`${prefix}/`);
        });
        if (hasActive) next.add(group.id);
      }
      return next;
    });
  }, [location.pathname]);

  const toggle = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* Vérification accès */
  const canSee = (section: string, fallback: (r: number) => boolean) => {
    if (accessLoading) return fallback(roleId);
    return hasAccessToSection(section);
  };

  /* Visibilité d'un groupe */
  const isGroupVisible = (group: NavGroup): boolean => {
    if (group.direct) {
      return canSee(group.section!, group.defaultAccess!);
    }
    const items = group.items ?? [];
    return items.some((item) => {
      if (item.requireReferentFunction) {
        if (roleId === 1) return true;
        return hasReferentFunction(item.requireReferentFunction);
      }
      return canSee(item.section, item.defaultAccess);
    });
  };

  /* Filtrage des sous-items */
  const visibleItems = (group: NavGroup) =>
    (group.items ?? []).filter((item) => {
      if (item.requireReferentFunction) {
        if (roleId === 1) return true;
        return hasReferentFunction(item.requireReferentFunction);
      }
      return canSee(item.section, item.defaultAccess);
    });

  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 pb-4 pt-3">
      {PANEL_NAV.map((group) => {
        if (!isGroupVisible(group)) return null;

        /* ── Item direct (Dashboard, Modération…) ── */
        if (group.direct) {
          const prefix = group.activePrefix ?? group.to!;
          const active = location.pathname === group.to || location.pathname.startsWith(`${prefix}/`);
          return (
            <Link
              key={group.id}
              to={group.to!}
              className={`relative flex items-center gap-3 rounded-md px-3 py-2.5 text-[13px] font-medium transition-colors ${
                active
                  ? 'bg-cockpit-green/12 text-cockpit-green before:absolute before:inset-y-1.5 before:-left-0.5 before:w-[2.5px] before:rounded-r before:bg-cockpit-green before:content-[\'\']'
                  : 'text-cockpit-text-2 hover:bg-cockpit-panel/60 hover:text-cockpit-text'
              }`}
            >
              <NavIcon name={group.icon} />
              <span className="truncate">{group.label}</span>
            </Link>
          );
        }

        /* ── Groupe accordion ── */
        const isOpen = openGroups.has(group.id);
        const subItems = visibleItems(group);
        if (!subItems.length) return null;

        const hasActiveChild = subItems.some((item) => {
          const prefix = item.activePrefix ?? item.to;
          return location.pathname === item.to || location.pathname.startsWith(`${prefix}/`);
        });

        return (
          <div key={group.id}>
            <button
              type="button"
              onClick={() => toggle(group.id)}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-[13px] font-medium transition-colors ${
                hasActiveChild && !isOpen
                  ? 'text-cockpit-text bg-cockpit-panel/60'
                  : 'text-cockpit-text-2 hover:bg-cockpit-panel/60 hover:text-cockpit-text'
              }`}
            >
              <NavIcon name={group.icon} />
              <span className="flex-1 truncate text-left">{group.label}</span>
              <Chevron open={isOpen} />
            </button>

            {isOpen && (
              <ul className="mt-0.5 ml-3 border-l border-cockpit-line-soft pl-3 space-y-0.5">
                {subItems.map((item) => {
                  const prefix = item.activePrefix ?? item.to;
                  const isEdit = item.to === '/panel/scenarios/gerer' && location.pathname.startsWith('/panel/scenarios/edit/');
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) => {
                          const active = isActive || isEdit;
                          return `flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] transition-colors ${
                            active
                              ? 'bg-cockpit-green/12 text-cockpit-text font-medium'
                              : 'text-cockpit-muted hover:bg-cockpit-panel/60 hover:text-cockpit-text-2'
                          }`;
                        }}
                        end={!item.activePrefix}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                          (location.pathname === item.to || location.pathname.startsWith(`${prefix}/`) || (item.to === '/panel/scenarios/gerer' && location.pathname.startsWith('/panel/scenarios/edit/')))
                            ? 'bg-cockpit-green'
                            : 'bg-cockpit-line'
                        }`} />
                        {item.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default PanelSidebarNav;
